<template>
	<core-layout-content full>
		<!-- Headline -->
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("reports") }}</template>
		</core-layout-headline-and-actions>

		<div class="flex xs-12 xs-left xs-middle bb1 bc7">
			<!-- Select Report Type dropdown -->
			<div class="xs-12 md-12">
				<core-form-input
					componentName="j-base-select"
					name="selectReportType"
					:options="options"
					v-validate="'required'"
					v-model="selectedType"
				/>
				<!-- Select VHIS template dropdown -->
				<select-vhis-template
					v-if="selectedType === 'vhis'"
					v-model="vhisTemplate"
				/>
			</div>
		</div>

		<!-- Filter -->
		<div class="xs-12">
			<core-filter
				:isExpanded="!isLoading"
				:for="selectedType"
				:submitButtonText="$t('download') + '.xlsx'"
				:forceReset="forceFilterReset"
				@submit="handleExport"
			/>
		</div>

		<!-- Loading -->
		<div
			class="flex wrap xs-12 xs-between mt2 mb2 bgWarning p4 pl2 pr2 c1 r2 fw7 fs3"
			v-if="isLoading"
		>
			{{ $t("preparing_your_report") }}
			<j-spinner size="small" />
		</div>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JSpinner from "@/components/JSpinner"
import api from "../api/"
import { mapState } from "vuex"
import XLSX from "xlsx"
import { convertToOctetStream } from "@/utils/"
import saveAs from "file-saver"
import individualsSchema from "../schemas/individualsSchema.js"
import companiesSchema from "../schemas/companiesSchema.js"
import policiesSchema from "../schemas/policiesSchema.js"
import quotesSchema from "../schemas/quotesSchema.js"
import commissionsSchema from "../schemas/commissionsSchema.js"
import commissionsModifier from "../modifiers/commissionsModifier.js"
import quotesNotesSchema from "../schemas/quotesNotesSchema.js"
import policiesNotesSchema from "../schemas/policiesNotesSchema.js"
import dayjs from "dayjs"
import CoreFilter from "@/modules/core/components/CoreFilter/"
import VhisSchemas from "../schemas/asiaSchema/index.js"
import SelectVhisTemplate from "./SelectVhisTemplate"
import AegSchemas from "../schemas/aegSchema/index.js"
import get from "lodash.get"

export default {
	components: {
		CoreLayoutContent,
		CoreFormInput,
		CoreLayoutHeadlineAndActions,
		JSpinner,
		CoreFilter,
		SelectVhisTemplate
	},
	data() {
		return {
			isLoading: false,
			selectedType: "policies",
			vhisTemplate: null,
			forceFilterReset: 0
		}
	},
	computed: {
		...mapState(["settings"]),

		settings() {
			return this.$store.state.settings
		},

		options() {
			let output = []

			// for default
			if (this.settings?.reports?.reporting?.hasDefault === true)
				output.push(
					{
						name: this.$t("policies"),
						value: "policies"
					},
					{
						name: this.$t("quotes"),
						value: "quotes"
					},
					{
						name: this.$t("clients_individuals"),
						value: "individuals"
					},
					{
						name: this.$t("clients_companies"),
						value: "companies"
					},
					{
						name: this.$t("commissions"),
						value: "commissions"
					},
					{
						name: this.$t("quotes_notes"),
						value: "quotes_notes"
					},
					{
						name: this.$t("policies_notes"),
						value: "policies_notes"
					}
				)

			// NOTE: We have to support old quotes flow for asia til further notice: this will be using old vhis reports
			if (["asia"].includes(this.$store.state.tenantId))
				output.push({ name: this.$t("vhis"), value: "vhis" })

			// for AEG
			if (this.settings?.reports?.reporting?.hasAEG === true)
				output.push(
					{
						name: this.$t("premium_bordereaux"),
						value: "abc_premium_bordereaux"
					},
					{
						name: this.$t("risk_london"),
						value: "abc_risk_london"
					},
					{
						name: this.$t("risk"),
						value: "abc_risk"
					},
					{
						name: this.$t("debit_note"),
						value: "abc_debit_note"
					},
					{
						name: this.$t("policy_renewal"),
						value: "abc_policy_renewal"
					},
					{
						name: this.$t("claim"),
						value: "abc_claim"
					},
					{
						name: this.$t("claim_bordereaux"),
						value: "abc_claim_bordereaux"
					},
					{
						name: this.$t("claims_paid_bordereaux"),
						value: "abc_claims_paid"
					},
					{
						name: this.$t("claims_outstanding_bordereaux"),
						value: "abc_claims_outstanding"
					}
				)

			return output
		}
	},

	methods: {
		async fetchTemplates() {
			const tenantId = this.$store.state.tenantId
			const res = await api.fetchTemplates({ tenantId })

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Store schema in reports store
			this.$store.commit("reports/SET_TEMPLATES", res.data)
		},

		async handleExport({ payload, where }) {
			console.log("payload", payload, "where:", where)
			// Show error helper function
			const showError = () => {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
			}

			// If some report is already in progress, prevent
			if (this.isLoading) return

			// Start loading
			this.isLoading = true

			//-----------------------------------------------------
			// Define different apiKeys depending on tab selected
			// ----------------------------------------------------
			// Default is for policies
			let apiKeys = {
				getCount: "numberOfPolicies",
				getData: "readAllPolicies",
				response: "policies"
			}
			let sort = { fieldName: "createdAt", type: "desc" }
			let finalWhere = where

			if (this.selectedType === "policies") {
				sort = { fieldName: "issueDate", type: "desc" }
			}

			if (
				this.selectedType === "quotes" ||
				this.selectedType === "quotes_notes"
			) {
				apiKeys = {
					getCount: "numberOfPolicies",
					getData: "readAllQuotes",
					response: "policies"
				}
				finalWhere = where
			}

			if (this.selectedType === "individuals") {
				apiKeys = {
					getCount: "numberOfIndividuals",
					getData: "readAllIndividuals",
					response: "individuals"
				}
				finalWhere = { and: [{ type_in: ["CUSTOMER"] }, where] }
			}

			if (this.selectedType === "companies") {
				apiKeys = {
					getCount: "numberOfCompanies",
					getData: "readAllCompanies",
					response: "companies"
				}
				finalWhere = { and: [{ type_in: ["CUSTOMER"] }, where] }
			}

			if (this.selectedType === "commissions") {
				apiKeys = {
					getCount: "numberOfPolicies",
					getData: "readAllPolicies",
					response: "policies"
				}
				finalWhere = where
			}

			// Decide whether to use readAllQuotes or readAllPolicies api call
			if (
				this.vhisTemplate === "template_01" ||
				this.vhisTemplate === "template_02" ||
				this.vhisTemplate === "template_03" ||
				this.vhisTemplate === "template_04" ||
				this.vhisTemplate === "template_a1" ||
				this.vhisTemplate === "template_a2"
			) {
				apiKeys = {
					getCount: "numberOfPolicies",
					getData: "readAllQuotes",
					response: "policies"
				}
				finalWhere = where
			}

			// for claims
			if (
				this.vhisTemplate === "template_11" ||
				this.vhisTemplate === "template_12"
			) {
				apiKeys = {
					getCount: "numberOfClaims",
					getData: "readAllClaims",
					response: "claims"
				}
			}

			//-----------------------------------------------------
			// Fetch number of items
			// ----------------------------------------------------
			let resNumberOfItems = await api[apiKeys.getCount]({
				where: finalWhere
			})
			if (resNumberOfItems.errors) {
				showError()
				return
			}

			let numberOfItems = resNumberOfItems.data[apiKeys.response].totalCount

			//-----------------------------------------------------
			//  Batch download of data from server
			// ----------------------------------------------------
			const limit = 100
			const numberOfBatches = Math.ceil(numberOfItems / limit).toFixed(0)

			// Prepare fetchedData
			let fetchedData = []

			// fetch data in batches, waiting for each call to finish before making another one
			for (let i = 0; i < numberOfBatches; i++) {
				const res = await api[apiKeys.getData]({
					limit,
					sort,
					skip: i * limit,
					where: finalWhere,
					tenantId: this.$store.state.tenantId
				})

				if (res.errors) {
					showError()
					return
				}

				// assign res to fetchedData
				fetchedData = [...fetchedData, ...res.data[apiKeys.response].list]
			}

			this.isLoading = false

			// Run some modifiers on data if necessary
			// E.g. commissions will flatten data
			if (this.selectedType === "commissions")
				fetchedData = commissionsModifier(fetchedData)

			// Fetch rejection settings if vhis template 11 or 12
			if (
				this.vhisTemplate === "template_11" ||
				this.vhisTemplate === "template_12"
			) {
				let res = await api.readClaimRejectionSettings({
					where: {
						productId: {
							plan: "S00027-01-000-02",
							type: "medical",
							version: null
						}
					}
				})
				const settings = get(
					res,
					"data.products_2.list[0].claimSettings.rejectionSettings.codeNames"
				)
				fetchedData = {
					list: fetchedData,
					settings: settings
				}
			}

			//-----------------------------------------------------
			//  Generate excel
			// ----------------------------------------------------
			// For xls generation we use sheetJS
			// https://docs.sheetjs.com/#streaming-write

			// Create a new workbook and set some props
			let workBook = XLSX.utils.book_new()
			const today = dayjs().format("DD-MM-YYYY_HH-mm-ss")
			const fileName =
				this.selectedType === "vhis"
					? `${this.selectedType}-${this.vhisTemplate}-${today}`
					: `${this.selectedType}-${today}`

			workBook.Props = {
				Title: fileName,
				Subject: `Report - ${this.selectedType}`,
				Author: "CoverGo",
				CreatedDate: new Date()
			}

			// Push a new sheet to the workbook
			workBook.SheetNames.push(this.selectedType)

			// Prepare data for policies
			// Init schema based on user select
			let schema = null
			if (this.selectedType === "policies")
				schema = policiesSchema(this, fetchedData)
			if (this.selectedType === "quotes")
				schema = quotesSchema(this, fetchedData)
			if (this.selectedType === "individuals")
				schema = individualsSchema(this, fetchedData)
			if (this.selectedType === "companies")
				schema = companiesSchema(this, fetchedData)
			if (this.selectedType === "commissions")
				schema = commissionsSchema(this, fetchedData)
			if (this.selectedType === "quotes_notes")
				schema = quotesNotesSchema(this, fetchedData)
			if (this.selectedType === "policies_notes")
				schema = policiesNotesSchema(this, fetchedData)

			// for vhis
			if (this.selectedType === "vhis")
				schema = VhisSchemas[this.vhisTemplate](fetchedData)

			//==================================================================
			// for aeg, regex check if selectedType first three characters are `aeg`//`abc`
			// Fetched data happening inside js file to fetch specific data for each report
			//==================================================================
			if (this.selectedType.match(/[abc]{3}/)?.length > 0)
				schema = await AegSchemas[this.selectedType](payload)

			// Creates workSheet from data in schema
			// Schema returns object { workSheetData, workSheetColumns, workSheetMerges }
			const workSheet = XLSX.utils.aoa_to_sheet(schema.workSheetData)

			// Apply Cell Column width settings
			workSheet["!cols"] = schema.workSheetColumns

			// Apply Cell Merges
			if (!workSheet["!merges"]) workSheet["!merges"] = []
			const merges = schema.workSheetMerges ? schema.workSheetMerges : []
			workSheet["!merges"] = [...merges]

			workBook.Sheets[this.selectedType] = workSheet

			const xlsBinary = XLSX.write(workBook, {
				bookType: "xlsx",
				type: "binary"
			})

			// Download
			// Prevent file download in cypress test
			if (!window.Cypress) {
				saveAs(
					new Blob([convertToOctetStream(xlsBinary)], {
						type: "application/octet-stream"
					}),
					`${fileName}.xlsx`
				)
			}
		}
	},

	created() {
		this.fetchTemplates()
	}
}
</script>
